import {
  useCallback, useState,
} from 'react';
import {
  DropdownComponent,
  DropdownItemComponent,
} from '~/_shared/baseComponents/dropdown';
import { InputSize } from '~/_shared/baseComponents/inputs';
import { LabelInCircleComponent } from '~/_shared/baseComponents/labelInShape/labelInCircleComponent';
import { Pill } from '~/_shared/baseComponents/pill/pill.component';
import { TooltipComponent } from '~/_shared/baseComponents/tooltip/tooltip.component';
import { uniqueValues } from '~/_shared/utils/array/array.helpers';
import { useTranslation } from '~/_shared/utils/hooks';
import type {
  FillConfigMetricInferenceSource, FillConfigMetricInferenceSourceInfo,
} from '~/_shared/utils/metric/metrics.types';
import { Trans } from '~/translations/Trans';

type MetricInferenceSourceFillConfigurationTagProps = {
  readonly inferenceSource: FillConfigMetricInferenceSourceInfo;
  readonly onFillClick: (source: FillConfigMetricInferenceSource) => void;
  readonly showFillNumbers: boolean;
};

export const MetricInferenceSourceFillConfigurationTagComponent = ({
  inferenceSource,
  onFillClick,
  showFillNumbers,
  ...restProps
}: MetricInferenceSourceFillConfigurationTagProps) => {
  const [t] = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const toggleDropdown = useCallback(() => {
    setIsDropdownOpen(isOpen => !isOpen);
  }, []);

  const closeDropdown = useCallback(() => {
    setIsDropdownOpen(false);
  }, []);

  if (inferenceSource.type === 'FillConfiguration' && inferenceSource.inferenceSources.length > 0) {
    const sortedSources = [...inferenceSource.inferenceSources].sort(
      (a, b) => a.fillOrderNumber - b.fillOrderNumber,
    );
    const lowestOrderFill = sortedSources[0];
    const fillNumbers = uniqueValues(sortedSources.map(source => source.fillOrderNumber)).join(',');

    if (lowestOrderFill) {
      return (
        <DropdownComponent
          isOpen={isDropdownOpen}
          triggerComponent={(
            <TooltipComponent
              tooltipContent={t('metricSource.inferredFillData')}
            >
              <Pill
                {...restProps}
                backgroundColor={lowestOrderFill.fillColor}
                textColor={lowestOrderFill.textColor}
                onClick={toggleDropdown}
                role="button"
                tabIndex={0}
              >
                {showFillNumbers ? (
                  <Trans
                    i18nKey="metricSource.fillNumber"
                    values={{ numbers: fillNumbers }}
                    components={{ span: <span /> }}
                  />
                ) : (
                  <Trans
                    i18nKey="metricSource.fill"
                  />
                )}
              </Pill>
            </TooltipComponent>
          )}
          onClose={closeDropdown}
        >
          {sortedSources.map(metricSourceInfo => {
            return (
              <DropdownItemComponent
                key={metricSourceInfo.fillOrderNumber}
                name={metricSourceInfo.fillName}
                size={InputSize.Medium}
                onClick={() => onFillClick(metricSourceInfo)}
                prefix={(
                  <LabelInCircleComponent
                    colors={{ fillColor: metricSourceInfo.fillColor, fontColor: metricSourceInfo.textColor }}
                    size={20}
                  >
                    {metricSourceInfo.fillOrderNumber}
                  </LabelInCircleComponent>
                )}
              />
            );
          })}
        </DropdownComponent>
      );
    }
  }

  return null;
};
